import {
  GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS,
  createMetricRegistry,
  type MetricRegistry,
  type MetricRegistryEntry,
} from './metric-registry';

// Objective-metric identity translation — graph program Phase 4.
//
// THE GAP THIS CLOSES. The objective-to-lever taxonomy names its objectives with
// versioned URNs (`apps/api/config/ask-helix/objective-taxonomy/objective-taxonomy-map.json`
// declares `metric:ebitda:v1` and `metric:operating_income:v1`), while the compiled
// governed metric registry names metrics with canonical keys and aliases
// (`ebitda_usd`, `operating_income_usd` + alias `operating_income`). Nothing translated
// between them as a shared, tested contract: the URN pattern existed only as an inline
// regex inside `apps/api/src/ask-helix/ask-helix.value-gap-bridge.runner.ts`, used
// there to build a reader LABEL — not to resolve identity.
//
// The consequence, measured: `metric:operating_income:v1` and `metric:ebitda:v1` both
// resolved to NOTHING in the governed registry. The taxonomy governed which levers may
// serve the EBITDA objective while that objective had no checked metric identity at
// all — the flagship of the program, unlinked. Phase 4's whole point is that every hop
// in the chain is id-checked, so the translation has to be one shared function with
// conformance over the REAL config, not a convention repeated per call site.
//
// WHAT THIS DOES. Parses the URN form, then resolves the bare name through the
// registry's own version-scoped resolution (which honours registered aliases). It does
// NOT invent mappings: no suffix guessing, no fuzzy matching, and no arbitrary picks —
// a name that reaches DIFFERENT canonical keys in different registry versions is
// reported ambiguous, never resolved to whichever version sorts first. If a bare name
// is not a canonical key or a registered alias anywhere, the caller sees the honest
// unlinked state, which is what conformance then reports.

/**
 * Canonical objective-metric URN: `metric:<bare_snake_name>:v<major>`.
 * The ONE authority for the URN grammar — consumers (including the value-gap-bridge
 * runner's reader labels) must use this export, never an inline copy.
 */
export const OBJECTIVE_METRIC_URN_PATTERN = /^metric:([a-z][a-z0-9_]*):v(\d+)$/i;

export type ParsedObjectiveMetricUrn = Readonly<{
  /** The bare metric name inside the URN, lower-cased. */
  bareMetricName: string;
  /** The URN's major version. */
  version: number;
}>;

/**
 * Parse an objective-metric URN. Returns null for anything that is not a URN —
 * including a bare metric name, which callers may still resolve directly.
 */
export function parseObjectiveMetricUrn(
  objectiveMetricId: string,
): ParsedObjectiveMetricUrn | null {
  const match = OBJECTIVE_METRIC_URN_PATTERN.exec(String(objectiveMetricId ?? '').trim());
  if (!match) return null;
  return { bareMetricName: match[1].toLowerCase(), version: Number(match[2]) };
}

function versionsOf(definitions: readonly MetricRegistryEntry[]): readonly string[] {
  return [
    ...new Set(
      definitions
        .map((entry) => entry.registryVersion)
        .filter((version): version is string => typeof version === 'string' && version.length > 0),
    ),
  ].sort();
}

/** Every registry version present in the compiled definition set. */
export function compiledRegistryVersions(): readonly string[] {
  return versionsOf(GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS);
}

export type ObjectiveMetricIdentityResolution =
  | Readonly<{
      status: 'resolved';
      /** The compiled definition the objective's metric identity points at. */
      entry: MetricRegistryEntry;
      /** Which compiled registry version resolved it. */
      registryVersion: string;
      /** The bare name that was looked up (URN-stripped when applicable). */
      lookupKey: string;
    }>
  | Readonly<{
      status: 'unresolved';
      lookupKey: string;
      reason: ObjectiveMetricIdentityUnresolvedReason;
    }>;

export type ObjectiveMetricIdentityUnresolvedReason =
  | 'not_a_registered_key_or_alias'
  // The name reaches DIFFERENT canonical keys in different registry versions.
  // Resolving it would mean picking one arbitrarily — fail-closed instead.
  | 'ambiguous_across_registry_versions'
  // The URN carries a major version no governed source declares. Resolving it
  // as if it were a declared version would invent a mapping — fail-closed.
  | 'unsupported_urn_version';

/**
 * The declared authority for objective-metric URN major versions. Every governed
 * source that names objective URNs today (objective-taxonomy-map.json,
 * objective-vocabulary.json) declares v1 only; shipped-config conformance
 * enumerates those files and fails if a new version appears without being added
 * here — extending this set is a deliberate, reviewed act, never an inference.
 */
export const SUPPORTED_OBJECTIVE_METRIC_URN_VERSIONS: readonly number[] = [1];

/**
 * Resolve an objective-metric id (URN or bare) against an explicit definition set.
 * The public compiled-registry entrypoint below memoizes; this uncached variant exists
 * so the ambiguity branch is unit-testable without touching the compiled constants.
 *
 * Resolution is VERSION-SCOPED across every version in the set, because the same
 * alias may be declared in more than one version — building a registry over the union
 * makes such aliases ambiguous and drops them (the alias-authority rule working
 * correctly), so a union-scoped lookup would wrongly report a resolvable metric as
 * missing. When versions DISAGREE on the canonical key, the id is reported ambiguous.
 */
export function resolveObjectiveMetricIdentityAgainst(
  definitions: readonly MetricRegistryEntry[],
  objectiveMetricId: string,
  registryForVersion?: (registryVersion: string) => MetricRegistry,
): ObjectiveMetricIdentityResolution {
  const parsed = parseObjectiveMetricUrn(objectiveMetricId);
  const lookupKey = parsed
    ? parsed.bareMetricName
    : String(objectiveMetricId ?? '').trim();
  if (parsed && !SUPPORTED_OBJECTIVE_METRIC_URN_VERSIONS.includes(parsed.version)) {
    return { status: 'unresolved', lookupKey, reason: 'unsupported_urn_version' };
  }

  const registryAt =
    registryForVersion ??
    ((registryVersion: string) =>
      createMetricRegistry(
        definitions.filter((entry) => entry.registryVersion === registryVersion),
      ));

  const hits: { entry: MetricRegistryEntry; registryVersion: string }[] = [];
  for (const registryVersion of versionsOf(definitions)) {
    const entry = registryAt(registryVersion).resolveMetric(lookupKey);
    if (entry) hits.push({ entry, registryVersion });
  }

  const canonicalKeys = new Set(hits.map((hit) => hit.entry.metricKey));
  if (canonicalKeys.size > 1) {
    return { status: 'unresolved', lookupKey, reason: 'ambiguous_across_registry_versions' };
  }
  if (hits.length > 0) {
    return { status: 'resolved', ...hits[0], lookupKey };
  }
  return { status: 'unresolved', lookupKey, reason: 'not_a_registered_key_or_alias' };
}

// Per-version registries over the immutable compiled definition set, built once on
// first use — identity resolution sits on request hot paths (drift catalog rows,
// graph projection) and must not reconstruct registries per lookup.
let compiledRegistriesByVersion: Map<string, MetricRegistry> | undefined;
function compiledRegistryForVersion(registryVersion: string): MetricRegistry {
  if (!compiledRegistriesByVersion) {
    compiledRegistriesByVersion = new Map(
      compiledRegistryVersions().map((version) => [
        version,
        createMetricRegistry(
          GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS.filter(
            (entry) => entry.registryVersion === version,
          ),
        ),
      ]),
    );
  }
  const registry = compiledRegistriesByVersion.get(registryVersion);
  if (!registry) {
    // Unreachable for versions produced by compiledRegistryVersions(); guard keeps
    // the memo honest if a caller ever passes a foreign version string.
    return createMetricRegistry([]);
  }
  return registry;
}

/**
 * Resolve an objective-metric id (URN or bare) to its compiled metric definition.
 * See resolveObjectiveMetricIdentityAgainst for the resolution and ambiguity law.
 */
export function resolveObjectiveMetricIdentity(
  objectiveMetricId: string,
): ObjectiveMetricIdentityResolution {
  return resolveObjectiveMetricIdentityAgainst(
    GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS,
    objectiveMetricId,
    compiledRegistryForVersion,
  );
}
